import { UploadCloud, Map as MapIcon, FileCheck2, PlayCircle, Eye, Rocket, ShieldCheck, GitBranch, ClipboardList, CalendarClock } from "lucide-react";
import useSEO from "@/components/site/useSEO";
import PageHero from "@/components/site/PageHero";
import { ScreenBlock, CTABand, SectionHead, Pill } from "@/components/site/ui";
import { Reveal, Stagger, StaggerItem } from "@/components/site/Reveal";
import { MigrationScreen } from "@/components/site/mockups";

const STEPS = [
  { icon: UploadCloud, label: "Upload", desc: "Bring in your data from CSV, XML, JSON and ZIP imports." },
  { icon: MapIcon, label: "Map", desc: "Map fields to AYOBELL with mapping confidence guidance." },
  { icon: FileCheck2, label: "Validate", desc: "Run validation and pre-flight checks against your data." },
  { icon: PlayCircle, label: "Simulate", desc: "Simulate the migration before anything goes live." },
  { icon: Eye, label: "Review", desc: "Identify critical issues and review outcomes with sign-off." },
  { icon: Rocket, label: "Migrate", desc: "Schedule go-live with rollback planning in place." },
];

const DETAILS = [
  { icon: MapIcon, title: "Mapping confidence", desc: "See how confidently source fields map to AYOBELL so you can focus attention where it matters." },
  { icon: FileCheck2, title: "Validation & pre-flight checks", desc: "Data is checked before go-live, surfacing issues early rather than after migration." },
  { icon: ClipboardList, title: "Critical issue identification", desc: "Clearly separate warnings from critical issues that need resolving before migrating." },
  { icon: ShieldCheck, title: "Separation of duties", desc: "Migration steps support appropriate review and sign-off across roles." },
  { icon: CalendarClock, title: "Scheduled go-live", desc: "Plan when your migration goes live so it fits around your service." },
  { icon: GitBranch, title: "Rollback planning", desc: "Go live with a plan in place, so you can move forward with confidence." },
];

export default function DataMigration() {
  useSEO(
    "Data Migration to AYOBELL | Care Data Migration Centre",
    "Move to AYOBELL with confidence. The AYOBELL Data Migration Centre supports CSV, XML, JSON and ZIP imports with mapping confidence, validation, simulation, review, scheduled go-live and rollback planning."
  );
  return (
    <>
      <PageHero
        eyebrow="Data Migration"
        title="Move to AYOBELL with confidence."
        subtitle="The AYOBELL Data Migration Centre helps you transform, map and validate your data — with migrations validated before go-live."
        primary={{ to: "/contact", label: "Book a Demo" }}
        secondary={{ to: "/platform", label: "Explore the Platform" }}
        screenComp={MigrationScreen}
      />

      <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHead center eyebrow="The workflow" title="Upload → Map → Validate → Simulate → Review → Migrate" desc="A structured path from your existing systems and paper records to AYOBELL." />
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4 mt-14">
            {STEPS.map((s, i) => (
              <StaggerItem key={s.label}>
                <div className="relative h-full rounded-2xl border border-slate-200 bg-slate-50 p-5 hover:border-teal-500/40 hover:bg-white hover:shadow-lg transition-all">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-teal-50 text-teal-600"><s.icon className="w-5 h-5" /></span>
                    <span className="font-mono text-xs text-slate-300 font-bold">0{i + 1}</span>
                  </div>
                  <h3 className="font-display font-bold text-base mt-4 text-slate-900">{s.label}</h3>
                  <p className="mt-1 text-xs text-slate-500 leading-relaxed">{s.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal className="mt-6 flex flex-wrap gap-2">
            {["CSV", "XML", "JSON", "ZIP imports", "Data transformation", "Migration reporting"].map((t) => <Pill key={t}>{t}</Pill>)}
          </Reveal>
        </div>
      </section>

      <section className="bg-slate-50 py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <ScreenBlock
            eyebrow="Migration Centre"
            title="Confidence at every step"
            desc="The Data Migration Centre gives you visibility into how your data maps and validates, so you can review and sign off before go-live."
            points={["Mapping confidence", "Validation & pre-flight checks", "Migration simulation", "Critical issue identification"]}
            screenKey="migration"
          />
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHead eyebrow="What makes it different" title="Built to migrate care data responsibly" desc="Migrations should be validated before go-live. AYOBELL is designed to make that the default." />
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {DETAILS.map((d) => (
              <StaggerItem key={d.title}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-xl hover:-translate-y-1 transition-all">
                  <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-teal-50 text-teal-600"><d.icon className="w-5 h-5" /></span>
                  <h3 className="font-display font-bold text-lg mt-4 text-slate-900">{d.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{d.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <CTABand title="Planning a move to AYOBELL?" desc="Talk to our team about migrating your care data with confidence." />
    </>
  );
}
